import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, RotateCcw, Lock, PlayCircle, Trash2, Loader2, ExternalLink, ClipboardCheck } from 'lucide-react';
import { stocktakeSessionsService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { getApiError } from '@/lib/utils';
import { resumePathForImport, isHistoryImport } from '@/lib/importResume';

// Wave 5A P3 — plain-English landing page for importing stocktake data.
// Historical Stocktake Conversion wave — adds resumable imports section.

// Set to true to hide the "Add a completed historical stocktake" new-entry card.
// Existing in-progress and completed imports (Continue/View sections) are always shown.
// Flip to false to re-enable the new-entry entry point. No restart or migration needed.
const _GATE_NEW_HISTORICAL_ENTRY = true;

// Friendly labels for both psb_import_runs.status (stage1_status from backend) and
// psb_conversion_runs.status (draft.status fallback). Keys cover actual DB values.
const STAGE_LABELS = {
  // psb_import_runs.status (actual values from Stage 1 scan)
  scanning: 'Scanning products',
  scanned: 'Products scanned',
  rescanned: 'Products re-scanned',
  dispatched: 'Dispatched',
  // psb_conversion_runs.status (conversion tracking status)
  started: 'Just started',
  stage1_scanning: 'Scanning products',
  stage1_complete: 'Products scanned',
  stage2_mapping: 'Reviewing product matches',
  stage3: 'All flagged items handled',
  stage4: 'Product matches accepted',
  stage5: 'Pricing applied',
  stage6: 'Finalised',
  stage7: 'Costs calculated',
  stage8_dispatching: 'Completing import',
};

function stageBadgeColour(status) {
  if (!status || status === 'started') return 'bg-slate-100 text-slate-700';
  if (status.includes('scanning')) return 'bg-blue-100 text-blue-700';
  if (status.includes('complete') || status.includes('mapping')) return 'bg-amber-100 text-amber-800';
  if (status.includes('dispatched') || status.includes('dispatch')) return 'bg-teal-100 text-teal-800';
  return 'bg-slate-100 text-slate-700';
}

function DraftImportCard({ draft, onContinue, onVoid, voidingId, isAdmin }) {
  const stageLabel = STAGE_LABELS[draft.stage1_status] || draft.status || 'In progress';
  const needsReview = draft.stage1_review_count > 0;
  const isVoiding = voidingId === draft.conversion_id;
  const readOnly = isHistoryImport(draft);
  return (
    <Card className="border hover:shadow-sm transition-shadow" data-testid={`draft-conversion-${draft.conversion_id}`}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{draft.archive_name || 'Unknown archive'}</p>
            {/* Identity row: site name shown to ALL stocktake users (not just admin)
                so Auditors can confirm which site the saved run belongs to.
                Owner/started-by and last-updated only shown to admin. */}
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 mb-1 text-xs text-muted-foreground">
              {draft.site_name && (
                <span data-testid={`draft-site-name-${draft.conversion_id}`}>
                  Site: {draft.site_name}
                </span>
              )}
              {/* Purpose shown to all users so same-site rows are clearly distinguishable */}
              {draft.import_route && (
                <span
                  className={`font-medium ${draft.import_route === 'route_a' ? 'text-sky-700' : 'text-violet-700'}`}
                  data-testid={`draft-purpose-${draft.conversion_id}`}
                >
                  {draft.import_route === 'route_a' ? 'Opening stock import' : 'Full historical import'}
                </span>
              )}
              {isAdmin && draft.owner_name && (
                <span data-testid={`draft-owner-name-${draft.conversion_id}`}>
                  Started by: {draft.owner_name}
                </span>
              )}
              {isAdmin && draft.import_type && (
                <span data-testid={`draft-import-type-${draft.conversion_id}`}>
                  {draft.import_type === 'Route A — Closing stock to opening'
                    ? 'Import closing stock'
                    : draft.import_type === 'Historical stocktake'
                    ? 'Full historical import'
                    : draft.import_type}
                </span>
              )}
              {isAdmin && draft.last_updated_at && (
                <span data-testid={`draft-last-updated-${draft.conversion_id}`}>
                  Last updated: {new Date(draft.last_updated_at).toLocaleDateString('en-GB')}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <Badge className={`text-[10px] px-1.5 py-0 ${stageBadgeColour(draft.stage1_status)}`}>
                {stageLabel}
              </Badge>
              {needsReview && (
                <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-800">
                  {draft.stage1_review_count} to review
                </Badge>
              )}
              {draft.is_dispatched && (
                <Badge className="text-[10px] px-1.5 py-0 bg-teal-100 text-teal-800">
                  <Lock className="h-2.5 w-2.5 mr-0.5" /> Completed
                </Badge>
              )}
            </div>
            {draft.archive_period_open && (
              <p className="text-xs text-muted-foreground mt-1">
                Period: {draft.archive_period_open} — {draft.archive_period_close || '…'}
              </p>
            )}
            {/* Only show "Started" date for non-admin users; admins see it in the meta row above */}
            {!isAdmin && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Started {draft.created_at ? new Date(draft.created_at).toLocaleDateString('en-GB') : '—'}
              </p>
            )}
          </div>
          <div className="flex gap-1.5 shrink-0">
            {/* Direct "Review product matches" action when Stage 1 is done and there are matches to review */}
            {draft.stage1_run_id && needsReview && !readOnly && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={() => onContinue(draft, 'review')}
                data-testid={`review-matches-${draft.conversion_id}`}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Review product matches
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="text-xs gap-1"
              onClick={() => onContinue(draft, 'continue')}
              data-testid={`continue-import-${draft.conversion_id}`}
            >
              <PlayCircle className="h-3.5 w-3.5" />
              {readOnly ? 'View' : 'Continue'}
            </Button>
            {/* Only show void for incomplete (non-dispatched) imports */}
            {!readOnly && (
              <Button
                size="sm"
                variant="ghost"
                className="text-xs gap-1 text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={() => onVoid(draft)}
                disabled={isVoiding}
                data-testid={`void-import-${draft.conversion_id}`}
                aria-label="Remove draft import"
                title="Remove draft import"
              >
                {isVoiding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />}
                <span className="sr-only">Remove draft import</span>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ImportStocktakeLandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role && ['admin', 'superadmin', 'owner', 'senior_scheduler'].includes(user.role);

  // Filter params forwarded by PsbStage1BPage's Back button — threaded back on Continue.
  const _retainViewMode       = searchParams.get('viewMode');
  const _retainFilterDecision = searchParams.get('filterDecision');
  const _retainSiteId         = searchParams.get('site_id');
  const _retainCategoryId     = searchParams.get('filterCategoryId');
  const _retainSubcatId       = searchParams.get('filterSubcatId');

  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [voidingId, setVoidingId] = useState(null);

  useEffect(() => {
    stocktakeSessionsService.listImportDrafts()
      .then(data => setDrafts(data.items || []))
      .catch(() => setDrafts([]))
      .finally(() => setDraftsLoading(false));
  }, []);

  const handleContinue = (draft, action) => {
    if (action === 'review' && draft.stage1_run_id) {
      // Direct link to Review product matches page — thread retained filter state back.
      const p = new URLSearchParams({ s1_run_id: draft.stage1_run_id });
      if (_retainSiteId)         p.set('site_id',         _retainSiteId);
      if (_retainViewMode)       p.set('viewMode',         _retainViewMode);
      if (_retainFilterDecision) p.set('filterDecision',   _retainFilterDecision);
      if (_retainCategoryId)     p.set('filterCategoryId', _retainCategoryId);
      if (_retainSubcatId)       p.set('filterSubcatId',   _retainSubcatId);
      navigate(`/stocktaking/psb-stage1b-mapping?${p.toString()}`);
    } else {
      navigate(resumePathForImport(draft));
    }
  };

  const handleVoid = async (draft) => {
    const archiveName = draft.archive_name || 'this import';
    if (!window.confirm(`Remove the front-door record for "${archiveName}"?\n\nAll scanned products and mapping evidence are preserved. Only the import tracking entry will be voided.`)) return;

    setVoidingId(draft.conversion_id);
    try {
      await stocktakeSessionsService.voidImportDraft(draft.conversion_id, 'Removed by user from landing page');
      setDrafts(prev => prev.filter(d => d.conversion_id !== draft.conversion_id));
      toast({ title: 'Import record removed', description: 'The import tracking record has been voided. All underlying data is preserved.' });
    } catch (err) {
      toast({ title: 'Could not remove', description: getApiError(err, 'Unable to void this import. It may have been dispatched or protected.'), variant: 'destructive' });
    } finally {
      setVoidingId(null);
    }
  };

  const activeDrafts = drafts.filter(d => !isHistoryImport(d));
  const completedDrafts = drafts.filter(isHistoryImport);

  return (
    <div className="space-y-6" data-testid="import-stocktake-landing-page">
      <Toaster />

      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/stocktaking/dashboard')}
          data-testid="back-to-stocktaking-dashboard"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Import stocktake data</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Choose how you want to bring stocktake data into AbbeyZone
          </p>
        </div>
      </div>

      {/* Continue an import — shown when there are active in-progress conversions */}
      {(draftsLoading || activeDrafts.length > 0) && (
        <div data-testid="continue-import-section">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardCheck className="h-4 w-4 text-amber-600" />
            <h2 className="text-base font-semibold text-slate-800">Continue an import</h2>
          </div>
          {draftsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading in-progress imports…
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {activeDrafts.map(draft => (
                <DraftImportCard
                  key={draft.conversion_id}
                  draft={draft}
                  onContinue={handleContinue}
                  onVoid={handleVoid}
                  voidingId={voidingId}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Completed imports (dispatched, read-only view) */}
      {completedDrafts.length > 0 && (
        <div data-testid="completed-imports-section">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="h-4 w-4 text-teal-600" />
            <h2 className="text-base font-semibold text-slate-800">Earlier imports (read-only)</h2>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {completedDrafts.map(draft => (
              <DraftImportCard
                key={draft.conversion_id}
                draft={draft}
                onContinue={handleContinue}
                onVoid={handleVoid}
                voidingId={voidingId}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        </div>
      )}

      {/* Choice cards */}
      <div>
        <h2 className="text-base font-semibold text-slate-800 mb-3">Start a new import</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="import-stocktake-choices">

          {/* Option 1: Set up site products and assign closing stock */}
          <Card className="border-2 hover:shadow-md transition-shadow" data-testid="import-choice-card-new-stocktake">
            <CardHeader>
              <div className="bg-emerald-600 w-12 h-12 rounded-lg flex items-center justify-center mb-2">
                <RotateCcw className="h-6 w-6 text-white" />
              </div>
              <CardTitle className="text-lg">Import closing stock from a PSB file</CardTitle>
              <CardDescription>
                Upload the PSB file from your last audit. We read the house, locations and closing stock, help you
                choose the site, and carry the closing stock into your next stocktake as opening stock.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/stocktaking/import/closing-stock">
                <Button className="w-full" data-testid="import-choice-button-new-stocktake">
                  Import closing stock from a PSB file
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Option 2: Add a completed historical stocktake — gated when _GATE_NEW_HISTORICAL_ENTRY is true */}
          {!_GATE_NEW_HISTORICAL_ENTRY && (
            <Card className="border-2 hover:shadow-md transition-shadow" data-testid="import-choice-card-historical-stocktake">
              <CardHeader>
                <div className="bg-violet-600 w-12 h-12 rounded-lg flex items-center justify-center mb-2">
                  <Lock className="h-6 w-6 text-white" />
                </div>
                <CardTitle className="text-lg">Add a completed historical stocktake</CardTitle>
                <CardDescription>
                  Import a completed legacy stocktake as a locked, read-only AbbeyZone session.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/stocktaking/import/historical-stocktake">
                  <Button className="w-full" data-testid="import-choice-button-historical-stocktake">
                    Add a completed historical stocktake
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
}
